import ApiService from '@/services/apiService';
import { EventBus } from '@/services/eventbus.js';
import moment from 'moment';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';

export default {
  data: function () {
    return {
      account: null,
      pageYOffset: 0,

      bridgeTypes: [],
      connectedBridge: null,
    };
  },
  computed: {
    operationalStatus() {
      return {
        0: this.$i18n_t('NiotaClassic.Devices.form.unset'),
        1: this.$i18n_t('NiotaClassic.Devices.form.inTransit'),
        2: this.$i18n_t('NiotaClassic.Devices.form.readyForOperation'),
        3: this.$i18n_t('NiotaClassic.Devices.form.inOperation'),
        4: this.$i18n_t('NiotaClassic.Devices.form.temporaryInactive'),
        5: this.$i18n_t('NiotaClassic.Devices.form.decommissioned'),
      };
    },
    operationalStatusItems() {
      return Object.keys(this.operationalStatus).map((status) => {
        return { text: this.operationalStatus[status], value: status };
      });
    },
    bridgeConfig() {
      const foundMatch = this.bridgeTypes.find((config) => {
        return config.type === (this.connectedBridge || {}).type;
      });
      return foundMatch;
    },
    bridgeCreateParameters() {
      const createDeviceMethod = get(this.bridgeConfig, 'manifest.functionScope.extension.devices.create');
      return (
        get(this.bridgeConfig, `manifest.commands.instance.${createDeviceMethod}.parameters.data.properties`) || {}
      );
    },
    bridgeUpdateMethod() {
      if (!this.connectedBridge) {
        return null;
      }
      return get(this.bridgeConfig, 'manifest.functionScope.extension.devices.update') || null;
    },
    bridgeReadMethod() {
      if (!this.connectedBridge) {
        return null;
      }
      return get(this.bridgeConfig, 'manifest.functionScope.extension.devices.read') || null;
    },
    bridgeCreateFields() {
      const createParameters = this.bridgeCreateParameters;
      let keys = Object.keys(createParameters).filter((key) => {
        // do not show the device-id as input-field
        if (key === 'device_id') {
          return false;
        }
        const visibleConfig = get(createParameters, `[${key}].visible`) || {};
        return Object.keys(visibleConfig).every((dependentKey) => {
          return this.twinObject.properties.bridge.deviceData[dependentKey] === visibleConfig[dependentKey];
        });
      });
      return keys.sort((a, b) => {
        const orderIdxA = get(createParameters, `[${a}].orderIdx`) || 0;
        const orderIdxB = get(createParameters, `[${b}].orderIdx`) || 0;
        if (orderIdxA < orderIdxB) {
          return -1;
        }
        if (orderIdxA > orderIdxB) {
          return 1;
        }
        return a.localeCompare(b);
      });
    },
    lastRawStateUpdate() {
      const isoDate = get(this.twinDataStates.find((state) => state.key === '_raw') || {}, 'value.time');
      if (isoDate) {
        return new Date(isoDate);
      }
      return null;
    },
  },
  watch: {
    twinDataStates() {
      if (this.twinDataStates.length) {
        if (this.shouldOpenStateDetail && this.$refs['stateTable']) {
          this.finishedLoadingStatesTable();
        }
      }
      this.twinObject = cloneDeep(this.twinObject);
    },
    $route() {
      // the component is not reloaded/re-mounted if the twin-id in the route changes
      // so perform the setup again for a different route; i.e. swap out the displayed details on-the-fly
      this.teardownDtwinSocket();
      this.setup();
      this.fetchBridges();
      if (get(this.$route, 'query.stateId')) {
        this.tabItem = 'tab-states';
      } else if (get(this.$route, 'query.ruleId')) {
        this.tabItem = 'tab-rules';
      }
    },
  },
  mounted() {
    window.addEventListener('scroll', this.saveScrollOffset);
    EventBus.$on('twin-finished-fetching', this.fetchBridges);
    this.setup();
  },
  destroyed() {
    window.removeEventListener('scroll', this.saveScrollOffset);
    EventBus.$off('twin-finished-fetching', this.fetchBridges);
  },

  methods: {
    saveScrollOffset() {
      this.pageYOffset = window.pageYOffset;
    },
    breadCrumbsPath() {
      const ancestorList = this.ancestorList();
      const accountTitle = get(ancestorList, '[0].tenantName', undefined);
      const accountId = get(ancestorList, '[0].tenantId', undefined);

      if (accountTitle && accountId) {
        this.account = `${accountTitle} (ID: ${accountId})`;
      }

      if (ancestorList.length) {
        const queryId = `${this.$route.params.id}`;
        return ancestorList.map((a) => {
          return {
            to: `/${`${a.id}` === queryId ? 'virtual-devices' : 'digitaltwin-mgmt'}/${a.id}`,
            text: a.title,
          };
        });
      }
      return [
        {
          to: '/virtual-devices/',
          text: this.$i18n_t('Common.drawer.virtualDevices'),
        },
      ];
    },

    // called after the twin has loaded and all `setup` methods in the EditMixin have been performed
    // jumping-point to perform further init-actions on the virtualdevice-twin
    fetchBridges() {
      this.connectedBridge = null;
      Promise.all([
        ApiService.getBridgeTypes().then((bridgeTypes) => {
          return bridgeTypes.filter((type) => {
            return Object.keys(get(type, 'manifest.commands.instance', {})).length > 0;
          });
        }),
        ApiService.getBridgesForTenant(this.twinObject.tenantId),
      ])
        .then((results) => {
          this.bridgeTypes = results[0];
          let validDeviceBridges = results[1].filter((bridgeInstance) => {
            const bridgeTypeDef = results[0].find((bridgeDef) => bridgeDef.type === bridgeInstance.type);
            // this bridge-type must have a create-functionality
            // otherwise it should have a read to get 1 single existing device as import
            if (
              get(bridgeTypeDef, 'manifest.functionScope.extension.devices.create') ||
              get(bridgeTypeDef, 'manifest.functionScope.extension.devices.read')
            ) {
              return true;
            }
            return false;
          });
          this.connectedBridge = validDeviceBridges.find(
            (bridge) => bridge.id === this.twinObject.properties.bridge.configId
          );
        })
        .catch((error) => {
          this.$snack(this.$i18n_t('Connectors.Connectors.errors.errorWhileFetchingConnectos'), 'error');
          console.error('Error fetching bridges', error);
        });
    },
    finishedLoadingStatesTable() {
      if (this.$route.query.stateId) {
        this.$nextTick(() => {
          const state = this.twinDataStates.find((s) => s.id === parseInt(this.$route.query.stateId));
          if (state) {
            this.shouldOpenStateDetail = false;
            this.$refs['stateTable'].editState(state);
          }
        });
      }
    },
    deviceConfigChanged() {
      // force a UI-refresh of the fields (dependent config fields)
      this.twinObject = cloneDeep(this.twinObject);
    },
    relativeTime(date) {
      if (date) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(date).fromNow();
      }
      return '-';
    },
    didUpdateStates() {
      this.refreshStates();
    },
    dialogDisable() {
      this.dtwinToggleState();
    },
    prepareSubmit() {
      this.handleSubmit();
    },
  },
};
